import { memo, useCallback, useState } from "react";

import {
  useDropdownButtonAria,
  useDropdownHelperTextAria,
  useDropdownLabelAria,
} from "@react/a11y/dropdownAria";
import type { IconName } from "@react/components/Icon";
import { useAccessibilityId } from "@react/hooks/useAccessibilityId";

import { DropdownView } from "./Dropdown.view";

export type DropdownOption = {
  iconName?: IconName;
  name: string;
  value: string | Record<string, unknown>;
};

type Props = {
  align?: "left" | "right";
  errorMessage?: string;
  fluid?: boolean;
  hasError?: boolean;
  helperText?: string;
  iconName?: IconName;
  isBorderless?: boolean;
  isDisabled?: boolean;
  labelPosition?: "left" | "top";
  onSelectOption: (option: DropdownOption) => void;
  options: DropdownOption[];
  selectedOption: DropdownOption;
} & (
  | { ariaLabel?: never; label: string }
  | { ariaLabel: string; label?: never }
);

const DropdownBase = ({
  align,
  ariaLabel,
  errorMessage,
  fluid,
  hasError = false,
  helperText,
  iconName,
  isBorderless,
  isDisabled,
  label,
  labelPosition,
  onSelectOption,
  options,
  selectedOption,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { helperTextId, inputId, labelId } = useAccessibilityId("dropdown");

  const handleSelectOption = useCallback(
    (option: DropdownOption) => {
      onSelectOption(option);
      setIsOpen(false);
    },
    [onSelectOption]
  );

  const buttonAria = useDropdownButtonAria({
    hasError,
    id: inputId,
    isOpen,
    ...(label ? { labelledBy: labelId } : { ariaLabel }),
    ...((hasError || helperText) && { describedBy: helperTextId }),
  });

  const labelAria = useDropdownLabelAria({
    labelId,
    targetId: inputId,
  });

  const helperTextAria = useDropdownHelperTextAria({
    helperTextId,
    hasError: hasError || false,
  });

  return (
    <DropdownView
      align={align}
      buttonAria={buttonAria}
      errorMessage={errorMessage}
      fluid={fluid}
      hasError={hasError}
      helperText={helperText}
      helperTextAria={helperTextAria}
      iconName={iconName}
      isBorderless={isBorderless}
      isDisabled={isDisabled}
      isOpen={isOpen}
      label={label}
      labelAria={labelAria}
      labelPosition={labelPosition}
      onSelectOption={handleSelectOption}
      onToggleOpen={setIsOpen}
      options={options}
      selectedOption={selectedOption}
    />
  );
};

export const Dropdown = memo(DropdownBase);
